import React, { useState, useEffect } from "react";
import SubscriptionService from "../services/SubscriptionService";
import { toast } from "react-toastify";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const SubscriptionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0], // Start of current year
    endDate: new Date().toISOString().split("T")[0], // Today
  });

  useEffect(() => {
    loadAnalytics();
  }, [period, dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      const [analyticsData, revenueData] = await Promise.all([
        SubscriptionService.getAnalytics(),
        SubscriptionService.getRevenueAnalytics({
          period,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      ]);

      setAnalytics(analyticsData.analytics);
      setRevenueAnalytics(revenueData.revenueAnalytics);
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const formatRevenueData = () => {
    if (!revenueAnalytics?.revenueByPeriod) return [];

    return revenueAnalytics.revenueByPeriod.map((item) => ({
      period:
        period === "monthly"
          ? `${item._id.year}-${String(item._id.month).padStart(2, "0")}`
          : `${item._id.year}-${String(item._id.month).padStart(
              2,
              "0"
            )}-${String(item._id.day).padStart(2, "0")}`,
      revenue: item.totalRevenue,
      count: item.count,
    }));
  };

  const formatStatusData = () => {
    if (!analytics?.statusBreakdown) return [];

    const colors = {
      ACTIVE: "#10B981",
      EXPIRED: "#EF4444",
      CANCELLED: "#6B7280",
      PENDING: "#F59E0B",
    };

    return Object.entries(analytics.statusBreakdown).map(([status, count]) => ({
      name: status,
      value: count,
      color: colors[status] || "#6B7280",
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Subscription Analytics
        </h2>

        <div className="flex space-x-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
              className="border border-gray-300 rounded-md px-3 py-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
              className="border border-gray-300 rounded-md px-3 py-1"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">
              Total Subscriptions
            </div>
            <div className="mt-2 text-3xl font-bold text-gray-900">
              {analytics.totalSubscriptions}
            </div>
            <div className="text-xs text-gray-500 mt-1">All time</div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">
              Active Subscriptions
            </div>
            <div className="mt-2 text-3xl font-bold text-green-600">
              {analytics.activeSubscriptions}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {analytics.totalSubscriptions > 0
                ? Math.round(
                    (analytics.activeSubscriptions /
                      analytics.totalSubscriptions) *
                      100
                  )
                : 0}
              % of total
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">
              Total Revenue
            </div>
            <div className="mt-2 text-3xl font-bold text-blue-600">
              ₹{analytics.totalRevenue?.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 mt-1">All time</div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">
              Average Revenue
            </div>
            <div className="mt-2 text-3xl font-bold text-purple-600">
              ₹
              {analytics.totalSubscriptions > 0
                ? Math.round(
                    analytics.totalRevenue / analytics.totalSubscriptions
                  )
                : 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">Per subscription</div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Revenue Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={formatRevenueData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip
                formatter={(value, name) => [
                  name === "revenue" ? `₹${value}` : value,
                  name === "revenue" ? "Revenue" : "Subscriptions",
                ]}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ fill: "#3B82F6" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Subscription Count Trend */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Subscription Count
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formatRevenueData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Status Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={formatStatusData()}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {formatStatusData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Drivers */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Top Spending Drivers
          </h3>
          <div className="space-y-3">
            {revenueAnalytics?.topDrivers?.slice(0, 5).map((driver, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {driver.driverName}
                  </div>
                  <div className="text-sm text-gray-500">{driver.phone}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">
                    ₹{driver.totalSpent}
                  </div>
                  <div className="text-sm text-gray-500">
                    {driver.subscriptionCount} subscriptions
                  </div>
                </div>
              </div>
            ))}
            {(!revenueAnalytics?.topDrivers ||
              revenueAnalytics.topDrivers.length === 0) && (
              <div className="text-center text-gray-500 py-8">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAnalytics;
